import React, { useState, useEffect } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonText,
  IonNote,
  IonAlert,
  IonIcon,
  IonToggle,
} from '@ionic/react';
import Toaster, { ToasterVariant } from '@/components/common/Toaster';
import { arrowBack, checkmark, close, pencilOutline, trashOutline } from 'ionicons/icons';
import { CAModalData, InspectionResultOption } from '@/types/model/corrective-action';
import { CorrectiveActionFormData, FileAttachment, AttachmentUpload } from '@/types/model/inspection-modal';
import { CorrectiveAction } from '@/types/model/inspection';
import CorrectiveActionCard from '../CorrectiveActionCard';
import { caOrchestrationService } from '@/services/orchestration/CAOrchestrationService';
import { apiService } from '@/services/ApiService';
import { CorrectiveActionSaveRequest } from '@/types/request/CorrectiveActionRequests';
import permissionService from '@/services/PermissionService';
import ModalFooter from '@/components/common/ModalFooter';
import { buildAttachmentOperations } from '@/services/util/attachmentOperations';

interface CorrectiveActionModalProps {
  isOpen: boolean;
  modalData: CAModalData | null;
  onClose: () => void;
  onSaved: () => void;
}

type ModalStep = 'select-group' | 'select-line-item' | 'editing';
type InternalMode = 'view' | 'edit' | 'add' | 'delete';
// ── Helpers ─────────────────────────────────────────────────────────────────

const toCorrectiveAction = (raw: CorrectiveActionFormData['id'] extends number ? any : any): CorrectiveAction => ({
  ...raw,
  Attachments: raw.Attachments?.map((a: any) => ({ ...a, Link: a.Link ?? undefined })) ?? null,
  AttachmentCount: raw.AttachmentCount ?? 0,
  ClassificationId: raw.ClassificationId ?? 0,
  ResponseAttachmentCount: raw.ResponseAttachmentCount ?? 0,
});

const initFormData = (raw: any, issuedByName: string): CorrectiveActionFormData => ({
  id: raw.Id,
  statusId: raw.StatusId,
  issuedOn: raw.Issued,
  issuedBy: issuedByName,
  classificationId: raw.ClassificationId ?? undefined,
  issue: raw.Issue ?? '',
  readyForResponse: false,
  attachments: (raw.Attachments ?? []).map((attachment: any, index: number): FileAttachment => ({
    type: attachment.Link ? 'link' : (attachment.FileId ? 'existing-resource' : 'local-file'),
    index,
    attachmentId: attachment.Id,
    fileId: attachment.FileId?.toString(),
    description: attachment.Description,
    link: attachment.Link,
    fileName: attachment.Filename,
  })),
  isValid: raw.IsValid,
  validatedOn: raw.ValidatedOn ?? undefined,
  validationComments: raw.ValidationComments ?? undefined,
  responses: undefined, // loaded lazily by CorrectiveActionCard
});

// ── Component ────────────────────────────────────────────────────────────────

const CorrectiveActionModal: React.FC<CorrectiveActionModalProps> = ({
  isOpen,
  modalData,
  onClose,
  onSaved,
}) => {
  const [step, setStep] = useState<ModalStep>('editing');
  const [internalMode, setInternalMode] = useState<InternalMode>('add');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<ToasterVariant>('success');

  // Context-selection state (step 1 & 2 when no context provided)
  const [availableGroups, setAvailableGroups] = useState<{ id: number; identifier: number; title: string }[]>([]);
  const [availableLineItems, setAvailableLineItems] = useState<{ id: number; lineItem: string; title: string }[]>([]);
  const [availableInspResults, setAvailableInspResults] = useState<InspectionResultOption[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [selectedLineItemId, setSelectedLineItemId] = useState<number | null>(null);
  const [selectedInspResultId, setSelectedInspResultId] = useState<number | null>(null);

  // CorrectiveActionCard state
  const [ca, setCa] = useState<CorrectiveAction | undefined>();
  const [formData, setFormData] = useState<CorrectiveActionFormData>({ issue: '', attachments: [] });
  const [originalFormData, setOriginalFormData] = useState<CorrectiveActionFormData>({ issue: '', attachments: [] });

  // Resolve effective context (either passed in or selected by user)
  const effectiveGroupTitle =
    availableGroups.find(g => g.id === selectedGroupId)?.title ?? modalData?.groupTitle ?? '';
  const effectiveLineItemId = selectedLineItemId ?? modalData?.lineItemId ?? null;
  const effectiveLineItem =
    availableLineItems.find(l => l.id === selectedLineItemId)?.lineItem ?? modalData?.lineItem ?? '';
  const effectiveLineItemTitle =
    availableLineItems.find(l => l.id === selectedLineItemId)?.title ?? modalData?.lineItemTitle ?? '';
  const effectiveInspResultId = selectedInspResultId ?? modalData?.inspectionResultId ?? null;
  const effectiveInspDate =
    availableInspResults.find(r => r.id === selectedInspResultId)?.date ?? modalData?.inspectionDate ?? null;

  // ── Initialise on open ────────────────────────────────────────────────────

  useEffect(() => {
    if (!isOpen || !modalData) return;

    // Reset state
    setIsSaving(false);
    setSelectedGroupId(null);
    setSelectedLineItemId(null);
    setSelectedInspResultId(null);
    setAvailableLineItems([]);
    setAvailableInspResults([]);

    if (modalData.isNew && !modalData.groupId) {
      // Mode 3 — new CA, no context: start at group selection
      setStep('select-group');
      setInternalMode('add');
      caOrchestrationService.getAvailableGroups().then(setAvailableGroups);
      setCa(undefined);
      const emptyForm: CorrectiveActionFormData = { issue: '', attachments: [] };
      setFormData(emptyForm);
      setOriginalFormData(emptyForm);
    } else {
      setStep('editing');
      caOrchestrationService.getAvailableGroups().then(setAvailableGroups);

      if (modalData.caItem) {
        // Mode 1 — view existing CA (opens in view mode)
        setInternalMode('view');
        setCa(toCorrectiveAction(modalData.caItem.raw));
        const fd = initFormData(modalData.caItem.raw, modalData.caItem.issuedByName);
        setFormData(fd);
        setOriginalFormData(fd);
      } else {
        // Mode 2 — new CA with pre-filled context
        setInternalMode('add');
        setCa(undefined);
        const emptyForm2: CorrectiveActionFormData = { issue: '', attachments: [] };
        setFormData(emptyForm2);
        setOriginalFormData(emptyForm2);
        // Pre-load inspection results for the known line item
        if (modalData.groupId && modalData.lineItemId) {
          setAvailableInspResults(
            caOrchestrationService.getInspectionResultsForLineItem(
              modalData.groupId,
              modalData.lineItemId,
            )
          );
        }
      }
    }
  }, [isOpen, modalData]);

  // ── Context-selection handlers ────────────────────────────────────────────

  const handleGroupSelect = (groupId: number) => {
    setSelectedGroupId(groupId);
    setSelectedLineItemId(null);
    setSelectedInspResultId(null);
    setAvailableInspResults([]);
    caOrchestrationService.getAvailableLineItems(groupId).then(setAvailableLineItems);
  };

  const handleLineItemSelect = (lineItemId: number) => {
    setSelectedLineItemId(lineItemId);
    setSelectedInspResultId(null);
    if (selectedGroupId) {
      //set latest 5 insp results for this line item
      setAvailableInspResults(
        caOrchestrationService.getInspectionResultsForLineItem(selectedGroupId, lineItemId).slice(0, 5)
      );
    }
  };

  const handleInspResultSelect = (inspResultId: number | null) => {
    setSelectedInspResultId(inspResultId);
    if (modalData) {
      modalData.inspectionResultId = inspResultId;
    }
  };

  const handleProceedToEdit = () => {
    setStep('editing');
  };
  const handleEdit = () => {
    setInternalMode('edit');
  };

  const handleDeleteMode = () => {
    setInternalMode('delete');
  };

  const handleDelete = async () => {
    const caId = modalData?.caItem?.id;
    if (!caId) return;
    setIsDeleting(true);
    try {
      await apiService.deleteCorrectiveAction(caId);
      onSaved();
      onClose();
    } catch (err) {
      console.error('Error deleting corrective action:', err);
      setToastMessage('Failed to delete corrective action. Please try again.');
      setToastVariant('error');
      setShowToast(true);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const hasUnsavedChanges = (): boolean => {
    return (
      formData.issue !== originalFormData.issue ||
      formData.classificationId !== originalFormData.classificationId ||
      formData.isValid !== originalFormData.isValid ||
      formData.validatedOn !== originalFormData.validatedOn ||
      formData.validationComments !== originalFormData.validationComments ||
      formData.readyForResponse !== originalFormData.readyForResponse ||
      formData.attachments.length !== originalFormData.attachments.length
    );
  };

  const handleCancel = () => {
    if (internalMode === 'delete') {
      setInternalMode('view');
      return;
    }
    if (hasUnsavedChanges()) {
      setShowConfirmClose(true);
    } else {
      setFormData(originalFormData);
      if (internalMode === 'edit') {
        setInternalMode('view');
      } else {
        onClose();
      }
    }
  };

  const handleClose = () => {
    if (internalMode !== 'view' && hasUnsavedChanges()) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };
  // ── Attachment upload helper ──────────────────────────────────────────────

  const uploadNewFiles = async (attachments: FileAttachment[]): Promise<Map<FileAttachment, AttachmentUpload>> => {
    const map = new Map<FileAttachment, AttachmentUpload>();
    for (const att of attachments) {
      if (!att.fileId && att.file) {
        const uploadResult = await apiService.uploadFile(att);
        map.set(att, { attachment: att, uploadResult });
      }
    }
    return map;
  };

  // ── Save ─────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (internalMode === 'add' && !effectiveLineItemId) return;
    setIsSaving(true);
    try {
      // Upload any new local-file attachments first
      const uploads = await uploadNewFiles(formData.attachments);
      const uploadedFileIds = new Map(
        Array.from(uploads.entries()).map(([attachment, upload]) => [attachment, upload.uploadResult.FileId]),
      );

      const siteId =
        internalMode === 'edit'
          ? (modalData?.caItem?.siteId ?? 0)
          : (availableInspResults.find(r => r.id === effectiveInspResultId)?.siteId ?? 0);

      const request: CorrectiveActionSaveRequest = {
        Id: internalMode === 'edit' ? (modalData?.caItem?.id ?? 0) : 0,
        Issue: formData.issue,
        ClassificationId: formData.classificationId ?? null,
        SiteId: siteId,
        ReadyForResponse: formData.readyForResponse ?? false,
        Attachments: buildAttachmentOperations({
          attachments: formData.attachments,
          originalAttachments: originalFormData.attachments,
          uploadedFileIds,
        }),
      };

      await apiService.saveCorrectiveAction(request);

      if (internalMode === 'edit') {
        setOriginalFormData(formData);
        setInternalMode('view');
        setToastMessage('Corrective action saved successfully.');
        setToastVariant('success');
        setShowToast(true);
      }
      onSaved();
    } catch (err) {
      console.error('Error saving corrective action:', err);
      setToastMessage('Failed to save corrective action. Please try again.');
      setToastVariant('error');
      setShowToast(true);
    } finally {
      setIsSaving(false);
    }
  };

  // ── Derived flags ─────────────────────────────────────────────────────────

  const isNewCA = modalData?.isNew ?? false;
  const canEditThisCA = modalData?.caItem
    ? permissionService.canEditCA(modalData.caItem.statusId, modalData.caItem.issuedById)
    : false;
  const canDeleteThisCA = modalData?.caItem
    ? permissionService.canDeleteCA(modalData.caItem.statusId, modalData.caItem.issuedById)
    : false;
  const canSave = internalMode === 'add'
    ? isNewCA && !!effectiveLineItemId && formData.issue.trim().length > 0
    : internalMode === 'edit'
      ? hasUnsavedChanges() && formData.issue.trim().length > 0
      : false;

  const fmt = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : '—');

  // ── Render ────────────────────────────────────────────────────────────────

  const renderContextSubHeader = () => {
    if (!effectiveGroupTitle && !effectiveLineItem) return null;
    return (
      <div
        style={{
          background: 'var(--ion-color-light-tint)',
          borderBottom: '1px solid var(--ion-color-light-shade)',
          padding: '8px 16px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          fontSize: '0.85rem',
        }}
      >
        {effectiveGroupTitle && (
          <div>
            <IonText color="medium" style={{ fontWeight: '600' }}>Group: </IonText>
            <IonText>{effectiveGroupTitle}</IonText>
          </div>
        )}
        {effectiveLineItem && (
          <div>
            <IonText color="medium" style={{ fontWeight: '600' }}>Line Item: </IonText>
            <IonText>{effectiveLineItem}{effectiveLineItemTitle ? ` — ${effectiveLineItemTitle}` : ''}</IonText>
          </div>
        )}
        {effectiveInspResultId && (
          <div>
            <IonText color="medium" style={{ fontWeight: '600' }}>Inspection: </IonText>
            <IonText>
              {fmt(effectiveInspDate)}
              {availableInspResults.find(r => r.id === effectiveInspResultId)?.siteName
                ? ` — ${availableInspResults.find(r => r.id === effectiveInspResultId)?.siteName}`
                : ''}
            </IonText>
          </div>
        )}
        {isNewCA && !modalData?.inspectionResultId && (
          <div>
            <IonText color="medium" style={{ fontWeight: '600' }}>Inspection: </IonText>
            <IonText color="medium" style={{ fontStyle: 'italic' }}>Not linked</IonText>
          </div>
        )}
      </div>
    );
  };

  const renderContextSelection = () => (
    <div style={{ padding: '16px' }}>
      <IonText color="medium" style={{ fontSize: '0.9rem', display: 'block', marginBottom: '16px' }}>
        Select the group and line item for this corrective action.
      </IonText>

      {/* Step 1 — Group */}
      <IonItem>
        <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Group *</IonLabel>
        <IonSelect
          value={selectedGroupId}
          placeholder="Select group"
          onIonChange={e => handleGroupSelect(e.detail.value)}
        >
          {availableGroups.map(g => (
            <IonSelectOption key={g.id} value={g.id}>
              {g.identifier}. {g.title}
            </IonSelectOption>
          ))}
        </IonSelect>
      </IonItem>

      {/* Step 2 — Line Item */}
      <IonItem style={{ marginTop: '8px' }}>
        <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Line Item *</IonLabel>
        <IonSelect
          value={selectedLineItemId}
          placeholder={selectedGroupId ? 'Select line item' : 'Select a group first'}
          disabled={!selectedGroupId}
          onIonChange={e => handleLineItemSelect(e.detail.value)}
        >
          {availableLineItems.map(li => (
            <IonSelectOption key={li.id} value={li.id}>
              {li.lineItem} — {li.title}
            </IonSelectOption>
          ))}
        </IonSelect>
      </IonItem>

      {/* Step 3 — Inspection Result (optional) */}
      {selectedLineItemId && (
        <IonItem style={{ marginTop: '8px' }}>
          <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Inspection Result (optional)</IonLabel>
          {availableInspResults.length === 0 ? (
            <IonNote style={{ paddingBottom: '8px' }}>
              No inspection results found for this line item.
            </IonNote>
          ) : (
            <IonSelect
              value={selectedInspResultId}
              placeholder="Select inspection result"
              onIonChange={e => handleInspResultSelect(e.detail.value)}
            >
              <IonSelectOption value={null}>None — not linked to an inspection</IonSelectOption>
              {availableInspResults.map(ir => (
                <IonSelectOption key={ir.id} value={ir.id}>
                  {fmt(ir.date)} — {ir.siteName}
                </IonSelectOption>
              ))}
            </IonSelect>
          )}
        </IonItem>
      )}

      <div style={{ marginTop: '24px' }}>
        <IonButton
          expand="block"
          disabled={!selectedGroupId || !selectedLineItemId}
          onClick={handleProceedToEdit}
        >
          Continue
        </IonButton>
      </div>
    </div>
  );

  const renderEditingStep = () => (
    <>
      {/* Ready For Response toggle */}
      <div
        style={{
          margin: '0 0 8px 0',
          padding: '4px 0',
          borderBottom: '1px solid var(--ion-color-light-shade)',
        }}
      >
        <IonItem lines="none">
          <IonLabel>
            <strong>Ready For Response</strong>
            <p style={{ fontSize: '0.8rem', color: 'var(--ion-color-medium)', margin: '2px 0 0' }}>
              Signals that a response can be added
            </p>
          </IonLabel>
          <IonToggle
            slot="end"
            checked={formData.readyForResponse ?? false}
            disabled={internalMode === 'view' || internalMode === 'delete'}
            onIonChange={e =>
              setFormData(prev => ({ ...prev, readyForResponse: e.detail.checked }))
            }
          />
        </IonItem>
      </div>

      <CorrectiveActionCard
        ca={ca}
        caIndex={0}
        formData={formData}
        globalFiles={modalData?.existingResources ?? []}
        onChange={partial => setFormData(prev => ({ ...prev, ...partial }))}
        readOnly={internalMode === 'view' || internalMode === 'delete'}
      />
    </>
  );

  return (
    <>
    <IonModal isOpen={isOpen} onDidDismiss={onClose} style={{ '--height': '90%', '--width': '95%', '--max-width': '1200px', '--border-radius': '16px' } as React.CSSProperties}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>
            {internalMode === 'add' ? 'New Corrective Action'
              : internalMode === 'edit' ? 'Edit Corrective Action'
              : 'View Corrective Action'}
          </IonTitle>
          <IonButtons slot="end">
            {internalMode === 'view' && (
              <>
                {canEditThisCA && (
                  <IonButton fill="clear" onClick={handleEdit}>
                    <IonIcon icon={pencilOutline} />
                  </IonButton>
                )}
                {canDeleteThisCA && (
                  <IonButton fill="clear" color="danger" onClick={handleDeleteMode}>
                    <IonIcon icon={trashOutline} />
                  </IonButton>
                )}
              </>
            )}
            <IonButton fill="clear" onClick={handleClose}>
              <IonIcon icon={close} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      {/* Context sub-header (visible in editing step) */}
      {step === 'editing' && renderContextSubHeader()}

      <IonContent className="ion-padding">
        {step === 'select-group' || step === 'select-line-item'
          ? renderContextSelection()
          : renderEditingStep()}
      </IonContent>

      {/* Footer — visible in add / edit / delete mode */}
      {step === 'editing' && internalMode !== 'view' && (
        <ModalFooter
          cancelLabel="Cancel"
          actionLabel={internalMode === 'delete' ? 'Delete' : 'Save'}
          cancelIcon={arrowBack}
          actionIcon={internalMode === 'delete' ? trashOutline : checkmark}
          onCancel={handleCancel}
          onAction={internalMode === 'delete' ? () => setShowDeleteConfirm(true) : handleSave}
          isCancelDisabled={isSaving || isDeleting}
          isActionDisabled={internalMode === 'delete' ? isDeleting : !canSave || isSaving}
          isActionLoading={internalMode === 'delete' ? isDeleting : isSaving}
          actionColor={internalMode === 'delete' ? 'danger' : 'primary'}
          cancelFill="outline"
        />
      )}
    </IonModal>

    <IonAlert
      isOpen={showConfirmClose}
      header="Discard Changes"
      message="You have unsaved changes. Are you sure you want to discard them?"
      buttons={[
        {
          text: 'Keep Editing',
          role: 'cancel',
          handler: () => setShowConfirmClose(false),
        },
        {
          text: 'Discard',
          role: 'destructive',
          handler: () => {
            setShowConfirmClose(false);
            setFormData(originalFormData);
            if (internalMode === 'edit') {
              setInternalMode('view');
            } else {
              onClose();
            }
          },
        },
      ]}
    />

    <IonAlert
      isOpen={showDeleteConfirm}
      header="Delete Corrective Action"
      message="Are you sure you want to permanently delete this corrective action? This action cannot be undone."
      buttons={[
        {
          text: 'Cancel',
          role: 'cancel',
          handler: () => setShowDeleteConfirm(false),
        },
        {
          text: 'Delete',
          role: 'destructive',
          handler: handleDelete,
        },
      ]}
    />

    <Toaster
      isOpen={showToast}
      onDidDismiss={() => setShowToast(false)}
      message={toastMessage}
      variant={toastVariant}
    />
    </>
  );
};

export default CorrectiveActionModal;
